import { useState } from 'react';
import Image from 'next/image';

import {
  ConnectedOutline,
  CubeOutline,
  FilledStack,
  GlobeFill,
  ImageOutline,
  IntegrationOutline,
  MemorySettings,
  StackOutline,
  TwoFaces,
} from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Stack, Typography } from '@lyearn/ui';

import ImageViewerModal from '../../../components/ImageViewerModal';
import EmptyState from '../../../KnowledgeAssetDetails/components/EmptyState';

interface CXArchitecturePageProps {
  solutionName: string;
  platformCapabilities: string[];
  productCapabilities: string[];
  typicalIntegrations: string[];
  integrationLandscapes: string[];
  platformCommonGrounds: string[];
  competitiveLandscape: string[];
  appToolsFunctions: string[];
  links: string[];
  journeyImage?: string;
  blueprintImage?: string;
}

const CXArchitecturePage = ({
  solutionName,
  platformCapabilities = [],
  productCapabilities = [],
  typicalIntegrations = [],
  integrationLandscapes = [],
  platformCommonGrounds = [],
  competitiveLandscape = [],
  appToolsFunctions = [],
  journeyImage,
  blueprintImage,
  links,
}: CXArchitecturePageProps) => {
  const [viewerImages, setViewerImages] = useState<string[]>([]);

  const journeyImageArray = journeyImage?.split('|').filter(Boolean) || [];
  const mainJourneyImage = journeyImageArray[0];
  const additionalJourneyImagesCount = journeyImageArray.length - 1;

  const blueprintArray = blueprintImage?.split('|').filter(Boolean) || [];
  const mainBlueprintImage = blueprintArray[0];
  const additionalBlueprintImagesCount = blueprintArray.length - 1;

  const topSections = [
    {
      title: 'Platform capabilities',
      icon: FilledStack,
      items: platformCapabilities || [],
    },
    {
      title: 'Product capabilities',
      icon: CubeOutline,
      items: productCapabilities || [],
    },
    {
      title: 'Typical integrations',
      icon: IntegrationOutline,
      items: typicalIntegrations || [],
    },
  ];

  const bottomSections = [
    {
      title: 'Integration Landscapes',
      icon: ConnectedOutline,
      items: integrationLandscapes || [],
    },
    {
      title: 'Platform Common Grounds',
      icon: StackOutline,
      items: platformCommonGrounds || [],
    },
    {
      title: 'Competitors',
      icon: TwoFaces,
      items: competitiveLandscape || [],
    },
    {
      title: 'App/Tools/Functions',
      icon: MemorySettings,
      items: appToolsFunctions || [],
    },
  ];

  return (
    <Stack className="max-w-[101.2rem] min-w-[75rem] mx-64 mt-48 mb-128" direction="col" gapY={21}>
      <Typography variant="display-xl">CX Architecture</Typography>

      <Stack className="grid grid-cols-3 gap-16">
        {topSections.map((section, index) => (
          <Stack
            key={index}
            className="border border-border rounded-12 p-20"
            direction="col"
            gapY={12}>
            <Stack alignItems="center" gapX={8}>
              <section.icon fillColorToken="icon-active" height={24} width={24} />
              <Typography variant="heading-xs">{section.title}</Typography>
            </Stack>
            <Stack direction="col">
              {section.items.length > 0 ? (
                section.items.map((item: string, itemIndex: number) => {
                  const [title = '', content = ''] = item.split('##').map((s) => s.trim());
                  const displayContent = content || item;
                  const displayTitle = title || '';

                  return (
                    <Stack
                      key={itemIndex}
                      className={cn(
                        'py-12',
                        itemIndex !== section.items.length - 1 && 'border-b border-border',
                      )}
                      direction="col"
                      gapY={4}>
                      {displayTitle && <Typography variant="label-01">{displayTitle}</Typography>}
                      <Typography className="text-text-secondary" variant="body-long-01">
                        {displayContent}
                      </Typography>
                    </Stack>
                  );
                })
              ) : (
                <EmptyState />
              )}
            </Stack>
          </Stack>
        ))}
      </Stack>

      <Stack className="grid grid-cols-3 gap-16">
        {bottomSections.map((section, index) => (
          <Stack
            key={index}
            className="border border-border rounded-12 p-20"
            direction="col"
            gapY={12}>
            <Stack alignItems="center" gapX={8}>
              <section.icon fillColorToken="icon-active" height={24} width={24} />
              <Typography variant="heading-xs">{section.title}</Typography>
            </Stack>
            <Stack direction="col">
              {section.items.length > 0 ? (
                section.items.map((item: string, itemIndex: number) => (
                  <Stack
                    key={itemIndex}
                    className={cn(
                      'py-12',
                      itemIndex !== section.items.length - 1 && 'border-b border-border',
                    )}
                    direction="col"
                    gapY={4}>
                    <Typography className="text-text-secondary" variant="body-long-01">
                      {item}
                    </Typography>
                  </Stack>
                ))
              ) : (
                <EmptyState />
              )}
            </Stack>
          </Stack>
        ))}
      </Stack>

      <Stack className="pt-32" direction="col" gapY={21}>
        <Typography variant="display-s">Architecture Image</Typography>
        <Stack className="grid grid-cols-2 gap-16">
          {mainJourneyImage ? (
            <Stack className="border border-border rounded-12 p-20" direction="col" gapY={16}>
              <Stack alignItems="center" direction="row" gapX={12}>
                <Stack alignItems="center" direction="row" gapX={8}>
                  <ImageOutline fillColorToken="icon-active" height={24} width={24} />
                  <Typography variant="heading-xs">
                    Journey Image
                    {additionalJourneyImagesCount > 0 ? ` +${additionalJourneyImagesCount}` : ''}
                  </Typography>
                </Stack>
                {additionalJourneyImagesCount > 0 && (
                  <Typography
                    className="cursor-pointer text-text-link hover:underline ml-auto"
                    variant="body-short-02"
                    onClick={() => setViewerImages(journeyImageArray)}>
                    View all
                  </Typography>
                )}
              </Stack>
              <div className="relative w-full h-[450px]">
                <Image
                  fill
                  alt="Journey"
                  className="rounded-12 bg-bg-secondary"
                  src={mainJourneyImage}
                  style={{ objectFit: 'contain' }}
                />
              </div>
            </Stack>
          ) : (
            <Stack className="border border-border rounded-12 p-20" direction="col" gapY={16}>
              <Stack alignItems="center" direction="row" gapX={8}>
                <ImageOutline fillColorToken="icon-active" height={24} width={24} />
                <Typography variant="heading-xs">Journey Image</Typography>
              </Stack>
              <EmptyState height="h-full min-h-[100px]" />
            </Stack>
          )}
          {mainBlueprintImage ? (
            <Stack className="border border-border rounded-12 p-20" direction="col" gapY={16}>
              <Stack alignItems="center" direction="row" gapX={12}>
                <Stack alignItems="center" direction="row" gapX={8}>
                  <ImageOutline fillColorToken="icon-active" height={24} width={24} />
                  <Typography variant="heading-xs">
                    Blueprints
                    {additionalBlueprintImagesCount > 0
                      ? ` +${additionalBlueprintImagesCount}`
                      : ''}
                  </Typography>
                </Stack>
                {additionalBlueprintImagesCount > 0 && (
                  <Typography
                    className="cursor-pointer text-text-link hover:underline ml-auto"
                    variant="body-short-02"
                    onClick={() => setViewerImages(blueprintArray)}>
                    View all
                  </Typography>
                )}
              </Stack>
              <div className="relative w-full h-[450px]">
                <Image
                  fill
                  alt="Blueprint"
                  className="rounded-12 bg-bg-secondary"
                  src={mainBlueprintImage}
                  style={{ objectFit: 'contain' }}
                />
              </div>
            </Stack>
          ) : (
            <Stack className="border border-border rounded-12 p-20" direction="col" gapY={16}>
              <Stack alignItems="center" direction="row" gapX={8}>
                <ImageOutline fillColorToken="icon-active" height={24} width={24} />
                <Typography variant="heading-xs">Blueprints</Typography>
              </Stack>
              <EmptyState height="h-full min-h-[100px]" />
            </Stack>
          )}
        </Stack>
      </Stack>

      <Stack className="self-start pt-32 w-full" direction="col" gapY={24}>
        <Typography className="pl-4" variant="display-s">
          URL
        </Typography>
        <Stack className="grid grid-cols-2 gap-16 xs:grid-cols-1 xs:w-full">
          <Stack
            className="border border-border rounded-12 min-h-[270px] w-full p-20 pb-24"
            direction="col">
            <Stack justifyContent="between">
              <Stack alignItems="center" gapX={8}>
                <GlobeFill fillColorToken="icon-active" height={18} width={18} />
                <Typography variant="heading-xs">Links</Typography>
              </Stack>
            </Stack>
            <Stack className="flex-1 mt-24 overflow-hidden" direction="col">
              {(links?.length ?? 0) > 0 ? (
                links.map((link, index) => (
                  <Stack
                    key={index}
                    className={cn(
                      'cursor-pointer group w-full',
                      index === links.length - 1 ? '' : 'border-b border-border pb-12 mb-12',
                    )}
                    onClick={() => window.open(link, '_blank')}>
                    <Typography
                      key={index}
                      className="text-link-external group-hover:text-link-external-hover"
                      variant="body-long-01">
                      {link}
                    </Typography>
                  </Stack>
                ))
              ) : (
                <EmptyState height="h-full min-h-[100px]" />
              )}
            </Stack>
          </Stack>
        </Stack>
      </Stack>

      <ImageViewerModal
        images={viewerImages}
        isOpen={viewerImages.length > 0}
        onClose={() => setViewerImages([])}
      />
    </Stack>
  );
};

export default CXArchitecturePage;
